/**
 * Create your account — post-payment registration (Reppr Elite access shell).
 *
 * This is not the mock's "request access" lead form: the real flow is
 * payment-first. A Stripe checkout creates a pending subscription; this page
 * validates that session, locks the email to the one that paid, and creates
 * the account against it. All of that logic is unchanged — only the
 * presentation moved onto AccessShell.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { register, pb } from '../lib/pocketbase'
import { planLabel } from '../lib/plans'
import { useSession } from '../hooks/useSession'
import { applyPendingSubscription, validatePendingSubscriptionForRegistration } from '../api/subscriptions'
import { acceptInvitationByToken } from '../api/invitations'
import { getSupportMailto, DEMO_BOOKING_URL } from '../lib/config'
import { PENDING_SUB_FAILED_KEY } from './Onboarding'
import PasswordStrength, { isPasswordStrong } from '../components/PasswordStrength'
import { showToast } from '../components/Toast'
import AccessShell, { AccessField, AccessBanner, AccessCtas, AccessAlt, AccessFoot } from '../components/access/AccessShell'
import styles from '../components/access/AccessShell.module.css'

export default function Register() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(true)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [validatedSubscription, setValidatedSubscription] = useState<{
    email: string
    plan: string
    subscriptionId?: string
    agencyId?: string
    agencyName?: string
  } | null>(null)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  const emailParam = searchParams.get('email')
  const { user } = useSession()

  const planName = planLabel(validatedSubscription?.plan)

  useEffect(() => {
    if (user) navigate('/dashboard')
  }, [navigate, user])

  // An invited colleague: the invitation names the email, the agency and
  // the role, so a password account can be created against it and the
  // invitation accepted in the same step. Before Sep 2026 an invitee with
  // no Google or Microsoft account had no way to sign up at all.
  const inviteToken = searchParams.get('invite')
  const [invited, setInvited] = useState<{ email: string; role: string; agencyName: string | null } | null>(null)
  useEffect(() => {
    if (!inviteToken || user) return
    let active = true
    setValidating(true)
    fetch('/api/invite-info', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: inviteToken }) })
      .then(async res => {
        const data = await res.json().catch(() => ({}))
        if (!active) return
        if (!res.ok) { setValidationError(data.error || 'That invitation could not be read.'); return }
        setInvited({ email: data.email, role: data.role, agencyName: data.agency_name ?? null })
        setEmail(data.email)
      })
      .catch(() => { if (active) setValidationError('Could not read the invitation. Try the link again.') })
      .finally(() => { if (active) setValidating(false) })
    return () => { active = false }
  }, [inviteToken, user])

  // Registration requires a validated payment — the account is created against
  // the subscription that paid for it.
  useEffect(() => {
    if (inviteToken) return
    let active = true
    async function validatePayment() {
      if (!sessionId && !emailParam) {
        if (!active) return
        setValidationError('No payment found. Accounts are created from a paid subscription, so complete payment first.')
        setValidating(false)
        return
      }
      try {
        const result = await validatePendingSubscriptionForRegistration(sessionId, emailParam)
        if (!active) return
        if (!result || !result.valid) {
          setValidationError('No valid subscription for this payment. The link may have expired (they last seven days), the account may already exist, or the payment is still processing.')
          setValidating(false)
          return
        }
        setValidatedSubscription({
          email: result.email ?? '',
          plan: result.plan,
          subscriptionId: result.subscriptionId,
          agencyId: result.agencyId,
          agencyName: result.agencyName,
        })
        setEmail(result.email ?? '')
      } catch {
        if (!active) return
        setValidationError('An error occurred while validating your payment. Try the link again, or contact support with your receipt.')
      } finally {
        if (active) setValidating(false)
      }
    }
    if (!user) validatePayment()
    return () => { active = false }
  }, [sessionId, emailParam, user, inviteToken])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    if (!isPasswordStrong(password)) {
      setError('Password is too weak. Use at least 8 characters with mixed case and numbers.')
      return
    }
    if (validatedSubscription && email.toLowerCase() !== validatedSubscription.email.toLowerCase()) {
      setError(`The account email must match the payment email: ${validatedSubscription.email}`)
      return
    }

    setError(null)
    setLoading(true)
    try {
      await register(email, password, name || null)

      if (inviteToken && invited) {
        // Accept the invitation now, then refresh the session so agency_id
        // (only carried on an auth response) is on the user before the
        // protected routes look at it.
        try {
          await acceptInvitationByToken(inviteToken)
          await pb.collection('users').authRefresh()
          navigate('/dashboard')
        } catch (inviteError: any) {
          sessionStorage.setItem('reppr_pending_invite', inviteToken)
          showToast(`Account created, but the invitation could not be accepted: ${inviteError.message}. Paste the code on the next screen.`, 'warning')
          navigate('/onboarding')
        }
        return
      }

      const currentUser = pb.authStore.model
      let hasPreAssignedAgency = false
      if (currentUser) {
        try {
          const result = await applyPendingSubscription(currentUser.id, email, sessionId)
          if (result.agencyId) hasPreAssignedAgency = true
          // agency_id was set server-side and is hidden from getOne; only an
          // auth response carries it. Without this refresh the protected
          // routes read an empty agency and bounce the new admin to the
          // invite-code screen until they reload.
          await pb.collection('users').authRefresh()
        } catch (subError) {
          console.error('Could not apply pending subscription:', subError)
          // Onboarding reads this and offers a retry instead of an invite-code
          // field the payer has no code for.
          sessionStorage.setItem(PENDING_SUB_FAILED_KEY, '1')
          showToast('Account created, but subscription linking failed. You can retry on the next screen.', 'warning')
        }
      }

      navigate(hasPreAssignedAgency ? '/dashboard' : '/onboarding')
    } catch (err: any) {
      setError(err.message || 'Failed to create the account')
    } finally {
      setLoading(false)
    }
  }

  if (validating) {
    return (
      <AccessShell
        eyebrow="Create your account"
        title="Checking your payment…"
        blurb="Matching this link to its subscription."
        width={460}
      >
        <AccessBanner title="Validating" body="This takes a second or two." />
      </AccessShell>
    )
  }

  if (validationError) {
    return (
      <AccessShell
        eyebrow="Create your account"
        title="We couldn’t match this link to a payment."
        blurb={validationError}
        width={480}
      >
        {/* Was "See plans and pricing" pointing at /#pricing - an anchor that
            does not exist on the landing page, so it dropped people at the top
            of the homepage. And pricing could not have helped them anyway:
            every route to a plan is a conversation now, so the page carries no
            way to pay. Somebody who lands here without a payment link needs
            the walkthrough. */}
        <AccessCtas
          primary="Book a walkthrough"
          primaryHref={DEMO_BOOKING_URL}
          secondary={<a href={getSupportMailto('Registration link')} className={styles.secondary}>Contact support</a>}
        />
        <AccessAlt prompt="Already have an account?">
          <Link to="/login">Sign in</Link>
        </AccessAlt>
        <AccessFoot>
          If you paid and this keeps failing, reply to your Stripe receipt email. It reaches
          a person, and your subscription is safe.
        </AccessFoot>
      </AccessShell>
    )
  }

  return (
    <AccessShell
      eyebrow={invited ? 'You are invited' : 'Payment received'}
      title="Create your account."
      blurb={invited
        ? `${invited.agencyName ? `${invited.agencyName} has` : 'An agency has'} invited you as ${invited.role === 'read only' ? 'a read-only member' : `an ${invited.role}`}. Set a password to join.`
        : validatedSubscription?.agencyName
          ? `Your ${planName} plan for ${validatedSubscription.agencyName} is paid. Set a password to open the Desk.`
          : `Your ${planName} plan is paid. Set a password to open the Desk.`}
      width={460}
    >
      <form onSubmit={handleSubmit} noValidate>
        <AccessField
          label="Work email"
          htmlFor="reg-email"
          aide={<span>{invited ? 'Locked to your invitation' : 'Locked to your payment'}</span>}
          hint={invited ? 'The address the invitation was sent to.' : 'The email that paid.'}
        >
          <input id="reg-email" type="email" value={email} readOnly autoComplete="email" />
        </AccessField>

        <AccessField label="Your name" htmlFor="reg-name">
          <input
            id="reg-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="First and last name"
            autoComplete="name"
          />
        </AccessField>

        <AccessField label="Password" htmlFor="reg-password">
          <input
            id="reg-password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </AccessField>
        {password && <PasswordStrength password={password} />}

        <AccessField label="Confirm password" htmlFor="reg-confirm">
          <input
            id="reg-confirm"
            type="password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </AccessField>

        {error && <div className={styles.error}>{error}</div>}

        <AccessCtas
          primary={loading ? 'Creating…' : 'Create account'}
          disabled={loading || !password || !confirm}
          meta={invited ? `${invited.agencyName?.toUpperCase() ?? 'AGENCY'} · INVITATION` : planName ? `${planName.toUpperCase()} PLAN · PAID` : undefined}
        />
      </form>

      <AccessAlt prompt="Already set up?">
        <Link to="/login">Sign in</Link>
      </AccessAlt>

      <AccessFoot>
        By creating an account you agree to the <Link to="/terms">terms of service</Link> and{' '}
        <Link to="/privacy">privacy policy</Link>.
      </AccessFoot>
    </AccessShell>
  )
}
